import { useEffect } from 'react';
import { LocalizedText, useSafeContext } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Highlight, Body, Stack, StackItem } from '@optimus-web/core';
import { WithAccountId, WithScope } from '@mews-commander/types';
import { getCustomerOrCompanyFullName } from '@mews-commander/legacy-entities/src/ducks/customers';
import { getUniqueBillOwnerIdsByBillIds } from '@mews-commander/legacy-entities/src/ducks';
import { useCommanderSelector } from '@mews-commander/utils';
import { canAssociateCurrentOwner, getSelectedItemsUniqueBillIds } from '../../ducks/itemAssignment';
import { AssociateOwnerCheckbox } from '../../components';
import { AssociateCurrentOwnerContext } from './context';
import { AccountBillList } from './AccountBillList';
import { AnotherAccountSearch } from './AnotherAccountSearch';

interface OwnProps extends WithScope, WithAccountId {
    isCompany: boolean;
    accountId: string;
    billId?: string;
    defaultAccountIds?: string[];
}

export const AnotherAccountSection = ({ billId, accountId, scope, isCompany, defaultAccountIds = [] }: OwnProps) => {
    const { associateCurrentOwner, toggleAssociateCurrentOwner } = useSafeContext(AssociateCurrentOwnerContext);
    const itemBillIds = useCommanderSelector((state) => getSelectedItemsUniqueBillIds(state, scope));
    const uniqueBillOwners = useCommanderSelector((state) =>
        getUniqueBillOwnerIdsByBillIds(state, { ids: itemBillIds })
    );
    const singleItemsOwner = uniqueBillOwners.length === 1;
    const newOwnerName = useCommanderSelector((state) => getCustomerOrCompanyFullName(state, { id: accountId }));
    const currentOwnerName = useCommanderSelector((state) =>
        singleItemsOwner ? getCustomerOrCompanyFullName(state, { id: uniqueBillOwners?.[0] }) : null
    );

    const canAssociateOwner = canAssociateCurrentOwner({ uniqueBillOwners, targetAccountId: accountId });

    const checked = canAssociateOwner ? associateCurrentOwner : false;
    const disabled = !canAssociateOwner;

    useEffect(() => {
        if (!canAssociateOwner) {
            toggleAssociateCurrentOwner(false);
        }
    }, [canAssociateOwner, toggleAssociateCurrentOwner]);

    const handleCheck = () => {
        toggleAssociateCurrentOwner(!associateCurrentOwner);
    };

    return (
        <Stack vertical spacing="200">
            <AnotherAccountSearch isCompany={isCompany} defaultAccountIds={defaultAccountIds} />
            {accountId ? (
                <>
                    <StackItem>
                        <Body size="small">
                            <LocalizedText textKey={TextKey.OwnedBillNewBillOwnerLabel} />
                        </Body>
                        <Highlight size="small">{newOwnerName}</Highlight>
                    </StackItem>
                    <StackItem>
                        <AssociateOwnerCheckbox
                            onChange={handleCheck}
                            ownerName={currentOwnerName ?? ''}
                            disabled={disabled}
                            checked={checked}
                        />
                    </StackItem>
                    <AccountBillList key={accountId} accountId={accountId} billId={billId} isCompany={isCompany} />
                </>
            ) : null}
        </Stack>
    );
};
